import { useEffect, useState } from "react";
import { onAuthStateChanged, reload, sendEmailVerification, signOut } from "firebase/auth";
import { LoaderCircle, LockKeyhole, MailCheck } from "lucide-react";
import { auth, authPersistenceReady } from "./firebase";
import { isAllowedEmail } from "./utils";
import AuthScreen from "./components/AuthScreen";
import Workspace from "./components/Workspace";

function LoadingScreen() {
  return (
    <main className="center-screen" aria-live="polite">
      <div className="brand-orb"><LoaderCircle className="spin" /></div>
      <p>LeadMagnet wird geladen …</p>
    </main>
  );
}

function RestrictedScreen({ user }) {
  return (
    <main className="center-screen">
      <div className="auth-card compact-card">
        <div className="auth-icon danger"><LockKeyhole /></div>
        <p className="eyebrow">Zugriff verweigert</p>
        <h1>Diese E-Mail ist nicht freigegeben.</h1>
        <p>Nur verifizierte Konten von marken-group.com und markenbreit.com erhalten Zugriff. Die Datenbankregeln blockieren dieses Konto.</p>
        <code>{user.email}</code>
        <button className="button primary" onClick={() => signOut(auth)}>Abmelden</button>
      </div>
    </main>
  );
}

function VerificationScreen({ user, onRefresh }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function resend() {
    setBusy(true);
    setMessage("");
    try {
      await sendEmailVerification(user);
      setMessage("Bestätigungs-E-Mail wurde erneut gesendet.");
    } catch (error) {
      setMessage(error.message || "E-Mail konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="center-screen">
      <div className="auth-card compact-card">
        <div className="auth-icon"><MailCheck /></div>
        <p className="eyebrow">Fast geschafft</p>
        <h1>E-Mail bestätigen</h1>
        <p>Wir haben einen Bestätigungslink an <strong>{user.email}</strong> geschickt. Danach hier erneut prüfen.</p>
        {message && <div className="notice">{message}</div>}
        <button className="button primary" onClick={onRefresh} disabled={busy}>Bestätigung prüfen</button>
        <button className="button ghost" onClick={resend} disabled={busy}>E-Mail erneut senden</button>
        <button className="text-button" onClick={() => signOut(auth)}>Mit anderem Konto anmelden</button>
      </div>
    </main>
  );
}

export default function App() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    let unsubscribe = () => {};
    authPersistenceReady.finally(() => {
      unsubscribe = onAuthStateChanged(auth, setUser);
    });
    return () => unsubscribe();
  }, []);

  async function refreshVerification() {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setUser({ ...auth.currentUser });
  }

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <AuthScreen />;
  if (!isAllowedEmail(user.email)) return <RestrictedScreen user={user} />;
  if (!user.emailVerified) return <VerificationScreen user={user} onRefresh={refreshVerification} />;
  return <Workspace user={user} />;
}
